// @ts-check
// Relatório do saneamento em texto para a escola (spec/email.md seção 19.2): "Removemos por segurança: 1 script,
// 3 atributos de evento, 1 formulário". Atributos on* contam como "de evento"; o resto aparece pelo nome.
import { t } from '../../i18n/index.js';

/** @typedef {import('../../../../shared/email-design/sanitize-email-html.js').SanitizeReport} SanitizeReport */

const ELEMENT_KEYS = new Set(['script', 'form', 'iframe', 'object', 'embed', 'style', 'link', 'meta', 'input', 'button', 'svg', 'video', 'audio', 'base']);

/**
 * Partes do relatório (cada uma já com a contagem) e o total de itens removidos.
 * @param {SanitizeReport | null | undefined} report
 * @returns {{ total: number, parts: string[] }}
 */
export function summarizeSanitizeReport(report) {
  if (!report) return { total: 0, parts: [] };
  /** @type {string[]} */
  const parts = [];
  let total = 0;
  for (const [name, count] of Object.entries(report.removedElements ?? {})) {
    if (!count) continue;
    total += count;
    parts.push(ELEMENT_KEYS.has(name)
      ? t(`emailDesign.html.elements.${name}`, { count })
      : t('emailDesign.html.otherElement', { count, name: `<${name}>` }));
  }
  let eventAttributes = 0;
  for (const [name, count] of Object.entries(report.removedAttributes ?? {})) {
    if (!count) continue;
    total += count;
    if (/^on/i.test(name)) eventAttributes += count;
    else parts.push(t('emailDesign.html.attribute', { count, name }));
  }
  if (eventAttributes > 0) parts.unshift(t('emailDesign.html.eventAttributes', { count: eventAttributes }));
  if (report.removedUrls > 0) {
    total += report.removedUrls;
    parts.push(t('emailDesign.html.urls', { count: report.removedUrls }));
  }
  if (report.removedStyles > 0) {
    total += report.removedStyles;
    parts.push(t('emailDesign.html.styles', { count: report.removedStyles }));
  }
  const plainComments = (report.removedComments ?? 0) - (report.removedConditionalComments ?? 0);
  if (report.removedConditionalComments > 0) {
    total += report.removedConditionalComments;
    parts.push(t('emailDesign.html.conditionalComments', { count: report.removedConditionalComments }));
  }
  if (plainComments > 0) {
    total += plainComments;
    parts.push(t('emailDesign.html.comments', { count: plainComments }));
  }
  return { total, parts };
}

/** Frase única do relatório, ou vazio quando nada saiu. @param {SanitizeReport | null | undefined} report */
export function sanitizeReportSentence(report) {
  const summary = summarizeSanitizeReport(report);
  if (summary.total === 0) return '';
  return t('emailDesign.html.removed', { list: summary.parts.join(', ') });
}
